/**
 * Warm-up calculators.
 *
 * Given a working weight (in the user's units) and exercise type, produce a
 * list of warm-up sets {weight, reps, rest_seconds, label}.
 *
 * Works offline — pure math, no network dependency.
 */

// Common barbell exercises where Prilepin-style warm-ups are standard practice.
// Matched case-insensitively against exercise name or equipment.
const BARBELL_KEYWORDS = [
  'barbell',
  'bench press',
  'squat',
  'deadlift',
  'overhead press',
  'military press',
  'clean',
  'snatch',
  'front squat',
  'back squat',
  'pendlay',
];

export const WARMUP_ALGORITHMS = {
  prilepin: 'prilepin',
  pyramid: 'pyramid',
  density: 'density',
};

export const WARMUP_ALGORITHM_LABELS = {
  prilepin: 'Prilepin (Strength)',
  pyramid: 'Simple Pyramid',
  density: 'Density (Endurance)',
};

export function isBarbellExercise({ name = '', equipment = '' } = {}) {
  const hay = `${name} ${equipment}`.toLowerCase();
  return BARBELL_KEYWORDS.some((kw) => hay.includes(kw));
}

export function defaultAlgorithmFor(exercise) {
  return isBarbellExercise(exercise) ? WARMUP_ALGORITHMS.prilepin : WARMUP_ALGORITHMS.pyramid;
}

/**
 * Round a weight to the nearest "loadable" increment.
 *  - Barbell (kg): round to 2.5kg to match standard plate math
 *  - Everything else: round to 0.5
 *  - lbs: round to 5lb when working with barbell, else 1lb
 */
function roundToIncrement(weight, { barbell, units }) {
  if (!isFinite(weight) || weight <= 0) return 0;
  const step = units === 'lbs' ? (barbell ? 5 : 1) : barbell ? 2.5 : 0.5;
  return Math.max(0, Math.round(weight / step) * step);
}

/**
 * Calculate warm-up sets.
 *
 * @param {{
 *   workingWeight: number,
 *   workingReps?: number,
 *   exercise?: { name?: string, equipment?: string, is_barbell?: boolean },
 *   algorithm?: 'prilepin' | 'pyramid' | 'density',
 *   units?: 'kg' | 'lbs',
 * }} input
 * @returns {Array<{weight: number, reps: number, rest_seconds: number, pct: number, label: string}>}
 */
export function calculateWarmupSets({
  workingWeight,
  workingReps = 5,
  exercise = {},
  algorithm,
  units = 'kg',
} = {}) {
  const alg = algorithm || defaultAlgorithmFor(exercise);
  const barbell = exercise.is_barbell ?? isBarbellExercise(exercise);

  if (!workingWeight || workingWeight <= 0) return [];

  let schema = [];
  if (alg === WARMUP_ALGORITHMS.prilepin) {
    schema = [
      { pct: 0.5, reps: 5, rest: 60, label: '50%' },
      { pct: 0.6, reps: 4, rest: 60, label: '60%' },
      { pct: 0.7, reps: 3, rest: 90, label: '70%' },
      { pct: 0.8, reps: 2, rest: 90, label: '80%' },
    ];
  } else if (alg === WARMUP_ALGORITHMS.pyramid) {
    schema = [
      { pct: 0.5, reps: 10, rest: 45, label: '50%' },
      { pct: 0.75, reps: 5, rest: 60, label: '75%' },
    ];
  } else if (alg === WARMUP_ALGORITHMS.density) {
    schema = [
      { pct: 0.3, reps: 15, rest: 30, label: '30%' },
      { pct: 0.6, reps: 8, rest: 45, label: '60%' },
    ];
  }

  // For very light working weights (< bar weight + 5kg), skip warm-ups
  // to avoid prescribing negative or silly sub-bar weights for barbell work.
  const barWeight = units === 'lbs' ? 45 : 20;
  if (barbell && workingWeight < barWeight + 5) {
    return [{
      weight: barWeight,
      reps: Math.max(5, Math.round(workingReps * 1.5)),
      rest_seconds: 60,
      pct: 0,
      label: 'Empty bar',
    }];
  }

  return schema.map((row) => ({
    weight: roundToIncrement(workingWeight * row.pct, { barbell, units }),
    reps: row.reps,
    rest_seconds: row.rest,
    pct: row.pct,
    label: row.label,
  }));
}
